import React, { useEffect, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import './AcademicAllLessons.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const toBackendClass = (value) => {
  const normalized = String(value || '').toUpperCase();
  if (!normalized) return '';
  if (['KG', 'LKG', 'UKG'].includes(normalized)) return normalized;
  return normalized.startsWith('CLASS-') ? normalized : `CLASS-${normalized}`;
};

const displayName = (value) => String(value || '').replace(/[_-]/g, ' ');

export default function AcademicAllLessons({ selectedClass, subjects = [] }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSubjectOverflow, setHasSubjectOverflow] = useState(false);
  const lessonScrollRefs = React.useRef({});
  const subjectColumnsRef = React.useRef(null);

  const subjectNames = useMemo(
    () => [...new Set(subjects.map((subject) => typeof subject === 'string' ? subject : subject.name)
      .filter((name) => name && String(name).toUpperCase() !== 'ALL'))],
    [subjects]
  );

  useEffect(() => {
    let cancelled = false;
    const classLevel = toBackendClass(selectedClass);

    if (!backendUrl || !classLevel || !subjectNames.length) {
      setGroups([]);
      return undefined;
    }

    setLoading(true);
    const loadSubject = async (subject) => {
      try {
        const bookTypesResponse = await fetch(`${backendUrl}/api/rest/academic/book-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ class_level: classLevel, subject }),
        });
        const bookTypesData = bookTypesResponse.ok ? await bookTypesResponse.json() : {};
        const bookType = bookTypesData.book_types?.[0] || '';
        if (!bookType) return { subject, bookType: '', lessons: [] };

        const lessonsResponse = await fetch(`${backendUrl}/api/rest/academic/unit-lessons`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ class_level: classLevel, subject, book_type: bookType }),
        });
        const lessonsData = lessonsResponse.ok ? await lessonsResponse.json() : {};
        const lessons = Array.isArray(lessonsData.unit_lessons) ? lessonsData.unit_lessons : [];
        return { subject, bookType, lessons };
      } catch (error) {
        console.error(`Unable to load ALL lessons for ${subject}:`, error);
        return { subject, bookType: '', lessons: [] };
      }
    };

    Promise.all(subjectNames.map(loadSubject)).then((nextGroups) => {
      if (!cancelled) setGroups(nextGroups);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [selectedClass, subjectNames]);

  useEffect(() => {
    const pageColumns = (event) => {
      const viewport = subjectColumnsRef.current;
      if (!viewport) return;
      const direction = event.detail?.direction || 1;
      viewport.scrollBy({
        left: direction * viewport.clientWidth,
        behavior: 'smooth',
      });
    };
    window.addEventListener('academic-subject-page', pageColumns);
    return () => window.removeEventListener('academic-subject-page', pageColumns);
  }, []);

  useEffect(() => {
    const viewport = subjectColumnsRef.current;
    if (!viewport) {
      setHasSubjectOverflow(false);
      return undefined;
    }

    const updateOverflow = () => {
      setHasSubjectOverflow(viewport.scrollWidth > viewport.clientWidth + 1);
    };
    updateOverflow();
    viewport.addEventListener('scroll', updateOverflow, { passive: true });
    window.addEventListener('resize', updateOverflow);

    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateOverflow);
    observer?.observe(viewport);
    if (viewport.firstElementChild) observer?.observe(viewport.firstElementChild);

    return () => {
      viewport.removeEventListener('scroll', updateOverflow);
      window.removeEventListener('resize', updateOverflow);
      observer?.disconnect();
    };
  }, [groups.length, loading]);

  const pageSubjects = (direction) => {
    const viewport = subjectColumnsRef.current;
    if (!viewport) return;
    viewport.scrollBy({
      left: direction * viewport.clientWidth,
      behavior: 'smooth',
    });
  };

  return (
    <div className="academicAllLessonsShell">
      {!loading && hasSubjectOverflow && (
        <button
          type="button"
          className="academicAllLessonsPageArrow academicAllLessonsPageArrow--previous"
          aria-label="Scroll subjects left"
          onClick={() => pageSubjects(-1)}
        >‹</button>
      )}
      <section className="academicAllLessons" aria-label="All subjects and lessons">
        {loading && <div className="academicAllLessonsStatus">Loading lessons for all subjects…</div>}
        {!loading && groups.length === 0 && (
          <div className="academicAllLessonsStatus">No subjects are available for this class.</div>
        )}
        {!loading && groups.length > 0 && (
          <div className="academicAllLessonsViewport" ref={subjectColumnsRef}>
            <div className="academicAllLessonsGrid">
              {groups.map(({ subject, bookType, lessons }) => (
                <article className="academicAllLessonsColumn" key={subject}>
                  <h3>{subject}</h3>
                  {lessons.length > 0 ? (
                    <ul ref={(element) => { lessonScrollRefs.current[subject] = element; }}>
                      {lessons.map((lesson) => (
                        <li key={lesson}>
                          <button
                            type="button"
                            className="academicLessonLink"
                            onClick={() => window.dispatchEvent(new CustomEvent('academic-all-lesson-selected', {
                              detail: { selectedClass, selectedSubject: subject, bookType, lesson },
                            }))}
                          >
                            {displayName(lesson)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="academicAllLessonsEmpty">No lessons available</p>
                  )}
                  <button
                    type="button"
                    className="academicLessonScrollArrow academicLessonScrollArrow--down"
                    aria-label={`Scroll ${subject} lessons down`}
                    onClick={() => lessonScrollRefs.current[subject]?.scrollBy({ top: 140, behavior: 'smooth' })}
                  >
                    <KeyboardArrowDownIcon />
                  </button>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
      {!loading && hasSubjectOverflow && (
        <button
          type="button"
          className="academicAllLessonsPageArrow academicAllLessonsPageArrow--next"
          aria-label="Scroll subjects right"
          onClick={() => pageSubjects(1)}
        >›</button>
      )}
    </div>
  );
}
